import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Type,
} from '@angular/core';
import {
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FieldTypeConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { FormlyFieldProps } from '@ngx-formly/material/form-field';
import { OutputExpression } from 'ng-dynamic-component';
import {
  combineLatest,
  Observable,
  ObservableInput,
  of,
  Subject,
  takeUntil,
} from 'rxjs';
import { defaults, transform } from 'lodash';

interface InputProps extends FormlyFieldProps {
  title: string;
  component: Type<unknown>;
  inputs: Record<string, unknown>;
  outputs: Record<string, OutputExpression>;
  visualOnly: boolean;
}

@Component({
    selector: 'lockton-apps-external-type',
    templateUrl: './external-type.component.html',
    styleUrls: ['./external-type.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ExternalTypeComponent
  extends FieldType<FieldTypeConfig<InputProps>>
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly internalForm: FormGroup = new FormGroup({});
  readonly destroyer$: Subject<void> = new Subject();
  inputs$!: Observable<Record<string, unknown>>;

  constructor(private readonly cdr: ChangeDetectorRef) {
    super();
  }

  ngOnInit(): void {
    const formlyRefInputs: Record<string, unknown> = {};

    if (!this.props.visualOnly) {
      formlyRefInputs['form'] = this.internalForm;
      formlyRefInputs['field'] = this.field;

      this.listenToChanges();
      this.setValidators();
    }

    this.buildInputs(defaults(formlyRefInputs, this.props.inputs));
    this.internalForm.setParent(this.formControl.parent);
  }

  private buildInputs(inputs: Record<string, unknown>): void {
    this.inputs$ = combineLatest(
      transform(
        inputs,
        (
          result: Record<string, ObservableInput<any>>,
          value: unknown,
          name: string
        ) => {
          result[name] = value instanceof Observable ? value : of(value);
        }
      )
    );
  }

  ngAfterViewInit(): void {
    this.formControl.updateValueAndValidity({ emitEvent: false });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.destroyer$.next();
    this.destroyer$.complete();
  }

  private listenToChanges(): void {
    this.internalForm.valueChanges
      .pipe(takeUntil(this.destroyer$))
      .subscribe(this.onInternalFormValueChange.bind(this));

    if (this.options.formState.validityCheck$) {
      this.options.formState.validityCheck$
        .pipe(takeUntil(this.destroyer$))
        .subscribe(this.onGlobalValidityCheck.bind(this));
    }
  }

  private onInternalFormValueChange(internalModel: unknown[]): void {
    if (this.internalForm.dirty) {
      this.formControl.markAsDirty();
    }

    this.formControl.patchValue(internalModel);
    this.form.updateValueAndValidity();
  }

  private setValidators(): void {
    const validators: ValidatorFn[] = [
      this.invalidAncestorsValidator.bind(this),
    ];

    if (this.props.required) {
      validators.push(Validators.required);
    }

    this.formControl.setValidators(validators);
  }

  private invalidAncestorsValidator(): ValidationErrors | null {
    return this.internalForm.invalid ? { invalidAncestors: true } : null;
  }

  private onGlobalValidityCheck(): void {
    this.internalForm.markAllAsTouched();
    this.internalForm.updateValueAndValidity();

    this.cdr.markForCheck();
  }
}
